import { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { SessionService } from '../services/SessionService';
import sessionStore from '../stores/SessionStore';

const withSessionHydration = <P extends object>(WrappedComponent: React.ComponentType<P>): FC<P> => {
  const SessionHydratedComponent: FC<P> = (props) => {
    const [isSessionHydrated, setIsSessionHydrated] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
      // If the session is already authenticated (e.g. LoginClerk just
      // bootstrapped it), skip the network round-trip and mark hydrated
      // immediately.  This eliminates the "Dashboard → spinner → Dashboard"
      // flash after login.
      if (sessionStore.isAuthenticated && sessionStore.user) {
        sessionStore.setIsLoading(false);
        setIsSessionHydrated(true);
        return;
      }

      sessionStore.setIsLoading(true);
      let cancelled = false;

      const hydrateSession = async () => {
        try {
          // Validate the session using the SessionService
          await SessionService.fetchCSRFToken();
          const validateSessionResponse = await SessionService.validateSession();
          if (cancelled) return;
          if (validateSessionResponse && validateSessionResponse.data.user) {
            sessionStore.setUser(validateSessionResponse.data.user);
            sessionStore.setIsAuthenticated(true);
          } else {
            sessionStore.clearSession();
            if (!location.pathname.startsWith('/login') && !location.pathname.startsWith('/crew/') && !location.pathname.startsWith('/crew-dual/') && !location.pathname.startsWith('/stream-test')) {
              navigate('/login');
            }
          }
        } catch (error) {
          if (cancelled) return;
          sessionStore.clearSession();
          if (!location.pathname.startsWith('/login') && !location.pathname.startsWith('/crew/') && !location.pathname.startsWith('/crew-dual/') && !location.pathname.startsWith('/stream-test')) {
            navigate('/login');
          }
        } finally {
          if (!cancelled) {
            sessionStore.setIsLoading(false);
            setIsSessionHydrated(true);
          }
        }
      };

      hydrateSession();

      return () => {
        cancelled = true;
      };
    }, []);

    if (!isSessionHydrated) {
      // Show a loading screen while the session is being hydrated TODO: Add a loading spinner
      return <div>Loading session...</div>;
    }

    // Render the wrapped component once the session is hydrated
    return <WrappedComponent {...props} />;
  };

  return SessionHydratedComponent;
};

export default withSessionHydration;
